import React, { Component } from 'react';
import { QMErrorStatuses, QMTypes } from '@quartermaster/qmlib-api';
import PropTypes from 'prop-types';
import { withRouter, Link } from 'react-router-dom';
import classNames from 'classnames';

import { connect } from 'react-redux';
import {
  authSignUp as actionAuthSignUp,
  ssoLogin,
} from '../../../redux/auth/actions';
import {
  selectSignUpType,
  selectSignUpInvite,
} from '../../../redux/auth/selectors';
import { clearSignupData } from '../../../redux/persistedSignupData/actions';

import { get, debounce } from 'lodash';

import {
  SIGNIN,
  TYPE_GENERIC_HOUSEHOLD,
  TYPE_HOUSEHOLD_INVITE,
  TYPE_OUTREACH,
} from '../config';
import { validateEmail } from '../../../util/validation';
import {
  fixVirtualKeyboardPadding,
  removeVirtualKeyboardPadding,
  debounceWrapper,
} from '../../../util/util';

import qmStyles from '../../../theme/qmStyles';
import containerStyles from './InfoForm.scss';

import OnboardingVerify from './OnboardingVerify';
import FormInput from '../../../components/FormInput';
import AddressAutocomplete from '../../../components/AddressAutocomplete/AddressAutocomplete';
import SSOContainer from '../../../components/SSOContainer';
import TermsAndPrivacy from '../components/TermsAndPrivacy';
import PersonalInfoProtectInfo from '../components/PersonalInfoProtectInfo';
import ContactUs from '../components/ContactUs';
import Loader from '../../../components/Loader';
import AddressErrorDisplay from '../components/AddressErrorDisplay';
import LaunchAreaNotificationButton from '../../../components/LaunchAreaNotification/LaunchAreaNotificationButton';
import SecuredFormInput from '../../../components/Form/SecuredFormInput';
import API from '../../../services/API';
import constants from '../../../constants';
import GenericInlineError from '../../../components/GenericInlineError';
import Google from '../../../components/SignOnButtons/Google';
import Apple from '../../../components/SignOnButtons/Apple';
import Facebook from '../../../components/SignOnButtons/Facebook';
import { getIdentityProviders } from '../../../services/singleSignOn';

const inputs = ['email', 'password', 'passwordConfirm', 'addressAutocomplete'];

const providerDefaults = {
  url: null,
  isLinked: false,
  accessToken: null,
};

class InfoForm extends Component {
  static propTypes = {
    navigateNext: PropTypes.func.isRequired,
    authSignUp: PropTypes.func.isRequired,
    clearPersistedSignupData: PropTypes.func.isRequired,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    location: PropTypes.shape({
      state: PropTypes.object,
    }).isRequired,
    type: PropTypes.string.isRequired,
    inviteData: PropTypes.object,
  };

  static defaultProps = {
    inviteData: null,
  };

  constructor(props) {
    super(props);

    inputs.forEach(input => {
      this[input] = React.createRef();
    });

    const { inviteData } = this.props;

    const { state } = props.location;

    const inviteRecipientName = get(inviteData, 'recipient.name', '');

    this.state = {
      showZip: false,
      zipCode: '',
      email: get(state, 'email') || get(inviteData, 'recipient.email', ''),
      address: get(state, 'address_line1') || '',
      firstName:
        get(state, 'first_name') || inviteRecipientName.split(' ')[0] || '',
      lastName:
        get(state, 'last_name') || inviteRecipientName.split(' ')[1] || '',
      password: '',
      passwordConfirm: '',
      passwordError: false,
      passwordErrorText: '',
      error: null, // can be any property inputted improperly
      loading: false,
      loadingSignOn: props.location.search.includes('code'),
      addressData: state || {},
      addressDataIsPreset: false,
      displayVerify: false,
      addPadding: false,
      providers: {
        apple: providerDefaults,
        facebook: providerDefaults,
        google: providerDefaults,
      },
      generalError: false,
      google_enabled: false,
      isDeactivated: get(inviteData, 'is_deactivated', false),
    };

    this.postData = debounce(this.postData, 1337, {
      leading: true,
      trailing: false,
    });
  }

  componentDidMount() {
    const { clearPersistedSignupData } = this.props;
    inputs.forEach(input => {
      fixVirtualKeyboardPadding(
        this[input].current,
        () => this.resizeContainer(true),
        () => this.resizeContainer(false),
      );
    });

    // @todo - switch to common package when https://bitbucket.org/quartermasterx/desktop-app-beta/pull-requests/317 ships
    API.Ability()
      .get()
      .then(({ data }) => {
        this.setState({ google_enabled: data['IDENTITY_INCLUDE_GOOGLE'] });
      });

    getIdentityProviders().then(({ data }) => {
      const providers = data.providers.reduce((prev, client) => {
        const provider = prev[client.name];
        return {
          ...prev,
          [client.name]: {
            ...provider,
            url: client.url_pattern.replace('{{ url }}', window.location.href),
          },
        };
      }, this.state.providers);

      this.setState(prevState => ({ ...prevState, providers }));
    });

    clearPersistedSignupData();
  }

  componentWillUnmount() {
    inputs.forEach(input => {
      removeVirtualKeyboardPadding(
        this[input].current,
        () => this.resizeContainer(true),
        () => this.resizeContainer(false),
      );
    });
  }

  resizeContainer = addPadding => {
    this.setState({ addPadding });
  };

  handleSSOSignIn = data => {
    this.setState({ loadingSignOn: false }, () => {
      this.props.ssoLogin(data);
      window.location = `/properties/${
        data.user.properties[0].id
      }/pro-list/dashboard`;
    });
  };

  handleSignUp = data => {
    if (data.access_token) {
      this.setState(prevState => ({
        ...prevState,
        loadingSignOn: false,
        firstName:
          (data.attributes &&
            data.attributes.google &&
            data.attributes.google.given_name) ||
          '',
        lastName:
          (data.attributes &&
            data.attributes.google &&
            data.attributes.google.family_name) ||
          '',
        email: (data.attributes && data.attributes.email) || '',
        providers: {
          ...prevState.providers,
          [data.provider]: {
            ...prevState.providers[data.provider],
            isLinked: true,
            accessToken: data.access_token,
          },
        },
      }));
    }
  };

  handleDisconnect = provider => () => {
    this.setState(prevState => ({
      ...prevState,
      providers: {
        ...prevState.providers,
        [provider]: {
          ...prevState.providers[provider],
          isLinked: false,
        },
      },
    }));
  };

  /**
   * @param type - state being modified
   * @param value - value to be used
   * @returns string value
   */
  formatInput = (type, value) => {
    if (type === 'zipCode') {
      if (value.length > 3) {
        const strippedString = value.replace(/ /g, '').slice(0, 6);
        return `${strippedString.slice(0, 3)} ${strippedString.slice(3, 6)}`;
      }
      return value;
    }
    return value;
  };

  setFactory = field => event => {
    const formattedInput = this.formatInput(field, event.target.value);
    this.setState(({ password, passwordConfirm }) => ({
      [field]: formattedInput,
      ...(field === 'password' && {
        passwordError: false,
        passwordErrorText: '',
      }),
      ...((field === 'passwordConfirm' || password === passwordConfirm) && {
        passwordConfirmError: false,
        passwordConfirmErrorText: '',
      }),
    }));
  };

  validatePassword = value => {
    if (!value) {
      return 'Enter a password';
    } else if (value.length < 9) {
      return 'Use 9 characters or more for your password';
    } else if (value.length > 128) {
      return 'Password length cannot exceed 128 characters';
    } else if (
      !/\d/.test(value) ||
      !/(?=.*?[!@#$%^&*()_+{}\[\]:;<>,.?~\-])/.test(value)
    ) {
      return 'Use at least one number and one symbol in your password';
    }
    return false;
  };

  validateData = () => {
    const {
      email,
      password,
      passwordConfirm,
      addressData,
      addressDataIsPreset,
    } = this.state;
    const { type } = this.props;

    if (!validateEmail(email)) return false;

    if (!this.isSkipPassword()) {
      const passwordErrorText = this.validatePassword(password);
      if (!!passwordErrorText) {
        this.setState({
          passwordError: true,
          passwordErrorText,
        });
        return false;
      }

      if (password !== passwordConfirm) {
        this.setState({
          passwordConfirmError: true,
          passwordConfirmErrorText: 'Those passwords didn’t match. Try again',
        });
        return false;
      }
    }

    if ((!addressData || !addressData.address) && !addressDataIsPreset) {
      return type === TYPE_HOUSEHOLD_INVITE;
    }
    if (this.addressAutocomplete && this.addressAutocomplete.current) {
      if (addressData && !addressData.street_number && !addressDataIsPreset) {
        return false;
      }
      if (addressData && !addressData.street && !addressDataIsPreset) {
        return false;
      }
    }

    return true;
  };

  postData = (e, verifyCode) => {
    e.preventDefault();
    if (!this.validateData()) return;
    const { authSignUp, history, type, inviteData } = this.props;
    const {
      firstName,
      lastName,
      email,
      password,
      addressData,
      isDeactivated,
    } = this.state;

    let property = {};

    if (type !== TYPE_HOUSEHOLD_INVITE) {
      property = {
        address_line1: addressData.address_line1 || addressData.address,
        city: addressData.city,
        google_place_id: addressData.place_id,
        province: addressData.province,
        postal: addressData.postal,
        street: addressData.street,
        ...(addressData.apt && { apt: addressData.apt }),
        geo_lat: addressData.geo_lat,
        geo_long: addressData.geo_long,
      };
    }

    const payload = {
      first_name: firstName,
      last_name: lastName,
      email,
      password,
      check_conflicts: true,
      block_property: type === TYPE_GENERIC_HOUSEHOLD,
      tos_acceptance: !isDeactivated,
      ...property,
    };

    if (verifyCode) {
      payload.outreach_code = verifyCode;
    }

    if (inviteData) {
      payload.invitation_token = inviteData.token;
    }

    if (this.isSkipPassword()) {
      payload.autogen_password = true;
      delete payload.password;
    }

    this.setState({ loading: true }, () => {
      authSignUp(payload)
        .then(data => {
          // This flag ensures that organic/first sign up users do not trigger our household verification flow
          // used by the outreach program when collecting referrals
          window.localStorage.setItem('qm.ignoreHouseholdVerification', true);

          this.setState({
            error: null,
            loading: false,
          });

          Object.keys(this.state.providers).forEach(provider => {
            const access_token = this.state.providers[provider].accessToken;
            if (access_token)
              API.SignupService().createIdentity({ provider, access_token });
          });

          if (data.property_conflict) {
            this.onError();
          } else {
            this.onSuccess();
          }
        })
        .catch(error => {
          if (error && error.data && error.data.user) {
            history.push(SIGNIN, { ...payload, ...error.data });
          } else {
            if (error.data && error.data.password && error.data.password[0]) {
              this.setState({
                passwordError: true,
                passwordErrorText: error.data.password[0],
                loading: false,
              });
            } else {
              this.setState({
                error: (error && error.data) || null,
                loading: false,
              });
            }
          }
        });
    });
  };

  onSuccess = () => {
    const { navigateNext } = this.props;
    window.ga('send', 'event', 'Registration', 'Complete');
    /* Meta Pixel Code for tracking at registration in the code */
    window.fbq('track', 'CompleteRegistration');
    navigateNext({ success: true });
  };

  onError = () => {
    const { navigateNext } = this.props;
    navigateNext({ error: true });
  };

  navigateVerify = e => {
    e.preventDefault();
    if (!this.validateData()) return;
    this.setState({
      displayVerify: true,
    });
  };

  navigateInfo = () => {
    this.setState({
      displayVerify: false,
    });
  };

  renderPasswordError = () => {
    const { passwordErrorText } = this.state;
    return <GenericInlineError errorDetails={passwordErrorText} />;
  };

  renderPasswordConfirmError = () => {
    const { passwordConfirmErrorText } = this.state;
    return <GenericInlineError errorDetails={passwordConfirmErrorText} />;
  };

  renderGeneralError = () => {
    return (
      <GenericInlineError
        errorDetails={
          <>
            Something went wrong. Need help?{' '}
            <a
              href={`mailto:${constants.QM_SUPPORT_EMAIL}`}
              className={classNames(
                containerStyles.passwordErrorText,
                containerStyles.errorLink,
              )}
            >
              Contact Us
            </a>
          </>
        }
      />
    );
  };

  renderVerify = () => {
    const { firstName, lastName, address, email, error, loading } = this.state;
    return (
      <OnboardingVerify
        firstName={firstName}
        lastName={lastName}
        address={address}
        email={email}
        postData={this.postData}
        navigateInfo={this.navigateInfo}
        loading={loading}
        error={error}
      />
    );
  };

  isPasswordlessSignup =
    this.props.inviteData && this.props.inviteData.passwordless_signup;

  isSkipPassword = () =>
    Object.keys(this.state.providers).some(
      key => this.state.providers[key].isLinked,
    ) || this.isPasswordlessSignup;

  render() {
    const { type } = this.props;
    const {
      email,
      address,
      addressData,
      addressDataIsPreset,
      firstName,
      lastName,
      showZip,
      zipCode,
      password,
      passwordConfirm,
      displayVerify,
      addPadding,
      passwordError,
      passwordConfirmError,
      error,
      loadingSignOn,
      providers,
      isDeactivated,
    } = this.state;

    if (displayVerify) {
      return this.renderVerify();
    }

    return (
      <form
        className={containerStyles.container}
        onSubmit={
          type === TYPE_OUTREACH
            ? this.navigateVerify
            : debounceWrapper(this.postData)
        }
        style={{ paddingBottom: addPadding ? 250 : 0 }}
      >
        {loadingSignOn && (
          <div className={containerStyles.loaderContainer}>
            <Loader />
          </div>
        )}
        <div className={containerStyles.mainForm}>
          <p className={containerStyles.signUptitle}>
            Create your Account on Quartermaster
          </p>
          {type !== TYPE_HOUSEHOLD_INVITE && !addressDataIsPreset && (
            <AddressAutocomplete
              onAddressChange={addressObj => {
                this.setState({
                  address: addressObj.full_address,
                  error: null,
                });
              }}
              address={address}
              addressData={addressData}
              onAddressFinalized={addressParts => {
                this.setState({ addressData: addressParts });
              }}
              inputStyle={{ marginBottom: qmStyles.gutter, paddingRight: 30 }}
              ref={this.addressAutocomplete}
              required
            />
          )}
          {type !== TYPE_HOUSEHOLD_INVITE && addressDataIsPreset && (
            <div
              style={{ display: 'flex', alignItems: 'center', width: '100%' }}
            >
              <div style={{ flex: 1 }}>
                <FormInput
                  value={`${addressData.street_number} ${addressData.street}`}
                  disabled
                  style={{ marginBottom: qmStyles.gutter, width: '100%' }}
                  label="Street"
                />
              </div>
              <div>
                <FormInput
                  value={`${get(addressData, 'apt', '')}`}
                  disabled
                  label="Apt"
                  style={{
                    marginBottom: qmStyles.gutter,
                    marginLeft: qmStyles.halfGutter,
                    minWidth: 81,
                    width: 20,
                  }}
                />
              </div>
            </div>
          )}
          {showZip && (
            <FormInput
              value={zipCode}
              label="Postal Code"
              onChange={this.setFactory('zipCode')}
              style={{ marginBottom: qmStyles.gutter }}
            />
          )}
          <div
            className={containerStyles.nameContainer}
            style={{ marginBottom: qmStyles.halfGutter }}
          >
            <div style={{ marginRight: qmStyles.halfGutter }}>
              <FormInput
                value={firstName}
                label="First Name"
                onChange={this.setFactory('firstName')}
                required
              />
            </div>
            <div>
              <FormInput
                value={lastName}
                label="Last Name"
                onChange={this.setFactory('lastName')}
                required
              />
            </div>
          </div>
          <FormInput
            type="email"
            value={email}
            ref={this.email}
            label="Email"
            onChange={this.setFactory('email')}
            style={{ marginBottom: qmStyles.halfGutter }}
            title="[name]@[address].[domain]"
            pattern={'[^@\\s]+@[^@\\s]+\\.[^@\\s]+'}
            required
          />
          {!this.isSkipPassword() && (
            <>
              <span
                className="font-normal text-14"
                style={{
                  width: '100%',
                  lineHeight: 'normal',
                  marginTop: qmStyles.halfGutter,
                }}
              >
                Set Password
              </span>
              <span
                className="text-14"
                style={{
                  lineHeight: 'normal',
                  marginBottom: qmStyles.halfGutter,
                }}
              >
                Passwords must be at least 9 characters and include at least one
                number and one symbol.
              </span>
              <SecuredFormInput
                ref={this.password}
                type="password"
                value={password}
                label="Password"
                onChange={this.setFactory('password')}
                style={{ marginBottom: qmStyles.halfGutter }}
                error={passwordError}
              />
              {passwordError && this.renderPasswordError()}
              <SecuredFormInput
                ref={this.passwordConfirm}
                type="password"
                value={passwordConfirm}
                label="Confirm Password"
                onChange={this.setFactory('passwordConfirm')}
                style={{
                  marginBottom: qmStyles.halfGutter,
                }}
                error={passwordConfirmError}
              />

              {passwordConfirmError && this.renderPasswordConfirmError()}
            </>
          )}
          {this.state.generalError && this.renderGeneralError()}
          {error && <AddressErrorDisplay error={error} />}
          <PersonalInfoProtectInfo />
          {this.renderSubmitButton()}
          {!this.isPasswordlessSignup && (
            <>
              <p
                style={{
                  marginBottom: qmStyles.gutter,
                  marginTop: qmStyles.gutter,
                  fontSize: '1.4rem',
                  fontWeight: 'normal',
                  lineHeight: 'normal',
                }}
              >
                Or Sign up with
              </p>
              {providers.apple.url && (
                <>
                  <SSOContainer
                    signOnText="Sign up with Apple"
                    serviceName="apple"
                    url={providers.apple.url}
                    isConnected={providers.apple.isLinked}
                    redirectUri={`${constants.BASE_URL}/app/signup/info`}
                    onError={() => this.setState({ generalError: true })}
                    onNeedsSignup={this.handleSignUp}
                    onDisconnect={this.handleDisconnect('apple')}
                    onSuccess={this.handleSSOSignIn}
                    renderButton={({
                      href,
                      onClick,
                      isConnected,
                      signOnText,
                    }) => (
                      <Apple
                        href={href}
                        onClick={onClick}
                        isConnected={isConnected}
                        signOnText={signOnText}
                      />
                    )}
                  />
                  <span style={{ marginTop: qmStyles.gutter }} />
                </>
              )}
              {this.state.google_enabled && providers.google.url && (
                <>
                  <SSOContainer
                    redirectUri={`${constants.BASE_URL}/app/signup/info`}
                    url={providers.google.url}
                    onSuccess={this.handleSSOSignIn}
                    onNeedsSignup={this.handleSignUp}
                    onDisconnect={this.handleDisconnect('google')}
                    onError={() => this.setState({ generalError: true })}
                    isConnected={providers.google.isLinked}
                    signOnText="Sign up with Google"
                    serviceName="google"
                    renderButton={({
                      href,
                      onClick,
                      isConnected,
                      signOnText,
                    }) => (
                      <Google
                        href={href}
                        onClick={onClick}
                        isConnected={isConnected}
                        signOnText={signOnText}
                      />
                    )}
                  />
                  <span style={{ marginTop: qmStyles.gutter }} />
                </>
              )}
              {providers.facebook.url && (
                <>
                  <SSOContainer
                    redirectUri={`${constants.BASE_URL}/app/signup/info`}
                    signOnText="Sign up with Facebook"
                    serviceName="facebook"
                    url={providers.facebook.url}
                    isConnected={providers.facebook.isLinked}
                    onError={() => this.setState({ generalError: true })}
                    onNeedsSignup={this.handleSignUp}
                    onDisconnect={this.handleDisconnect('facebook')}
                    onSuccess={this.handleSSOSignIn}
                    renderButton={({
                      href,
                      onClick,
                      isConnected,
                      signOnText,
                    }) => (
                      <Facebook
                        href={href}
                        onClick={onClick}
                        isConnected={isConnected}
                        signOnText={signOnText}
                      />
                    )}
                  />
                  <span style={{ marginTop: qmStyles.gutter }} />
                </>
              )}
            </>
          )}
          {!isDeactivated && <TermsAndPrivacy />}
          <ContactUs />
          <div className={containerStyles.signInContainer}>
            <p className={containerStyles.signInTitle}>
              Already on Quartermaster
              <Link className={containerStyles.signInLink} to={SIGNIN}>
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </form>
    );
  }

  renderSubmitButton = () => {
    const {
      loading,
      error,
      addressData,
      email,
      firstName,
      lastName,
    } = this.state;
    if (
      error &&
      error.errorCode ===
        QMErrorStatuses[QMTypes.PROPERTY].PROPERTY_OUT_OF_SCOPE
    ) {
      return (
        <LaunchAreaNotificationButton
          payload={{
            ...addressData,
            first_name: firstName,
            last_name: lastName,
            email,
          }}
        />
      );
    }

    return !loading ? (
      <FormInput
        type="submit"
        value="Sign up"
        style={{
          backgroundColor: qmStyles.qmBabyGreen,
          width: '100%',
          color: 'white',
          lineHeight: 0,
          fontWeight: 'normal',
          border: 'none',
        }}
      />
    ) : (
      <div
        style={{
          height: 40,
          backgroundColor: 'white',
          border: `1px solid ${qmStyles.greyDark}`,
          width: '100%',
        }}
      >
        <Loader />
      </div>
    );
  };
}

const mapStateToProps = state => ({
  type: selectSignUpType(state),
  inviteData: selectSignUpInvite(state),
});

const bindAction = {
  ssoLogin,
  authSignUp: actionAuthSignUp,
  clearPersistedSignupData: clearSignupData,
};

export default withRouter(
  connect(
    mapStateToProps,
    bindAction,
  )(InfoForm),
);
